/**
 * Typed client for `GET|PATCH /v1/sites/:site_id/notification-preferences`
 * (founder request 2026-10-20 — ADR-0245-successor security alerts v2).
 *
 * Mirrors the discriminated-union pattern of the other site-scoped
 * bags: every call resolves to a `kind` result; nothing throws. The
 * default-on contract matches the server (`siteNotifPrefResolved`):
 * absent keys read as `true`.
 */

import { ApiError, type HttpClient } from './http-client.js';

export const SECURITY_V2_PREF_KEYS = [
  'security_got_through_email',
  'security_got_through_slack',
  'security_new_campaign_email',
  'security_new_campaign_slack',
  'security_weekly_digest',
] as const;

export type SecurityV2PrefKey = (typeof SECURITY_V2_PREF_KEYS)[number];

/**
 * Full per-site notification preference key set for the consolidated
 * Notifications page (founder brief 2026-10-20). Mirror of the server's
 * `SITE_NOTIFICATION_PREF_KEYS` export; the server narrows PATCH
 * payloads to the same list.
 */
export const SITE_NOTIFICATION_PREF_KEYS = [
  'escalation_email',
  'escalation_slack',
  'agent_weekly_email',
  'manifest_change_email',
  'manifest_change_slack',
  'security_got_through_email',
  'security_got_through_slack',
  'security_new_campaign_email',
  'security_new_campaign_slack',
  'security_weekly_digest',
] as const;

export type SiteNotificationPrefKey = (typeof SITE_NOTIFICATION_PREF_KEYS)[number];

export interface SiteNotificationRecipient {
  readonly user_id: string;
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly receives: boolean;
}

export interface SiteNotificationPreferences {
  /**
   * Resolved preferences for every per-site notification key. The
   * server coerces missing keys to `true` (default-on) before writing
   * this record, so a UI never has to carry the default-on contract.
   */
  readonly preferences: Record<SiteNotificationPrefKey, boolean>;
  readonly recipients: readonly SiteNotificationRecipient[];
  readonly can_edit: boolean;
}

export type SiteNotificationPreferencesResult =
  | { readonly kind: 'ok'; readonly value: SiteNotificationPreferences }
  | { readonly kind: 'not_found' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'bad_request'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export async function getSiteNotificationPreferences(
  http: HttpClient,
  site_id: string,
): Promise<SiteNotificationPreferencesResult> {
  try {
    const body = await http.request<SiteNotificationPreferences>({
      method: 'GET',
      path: `/v1/sites/${encodeURIComponent(site_id)}/notification-preferences`,
    });
    if (body === undefined) {
      return { kind: 'error', message: 'empty_response' };
    }
    return { kind: 'ok', value: body };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') return { kind: 'bad_request', message: err.message };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'network_error',
    };
  }
}

export async function patchSiteNotificationPreferences(
  http: HttpClient,
  site_id: string,
  patch: Partial<Record<SiteNotificationPrefKey, boolean>> & {
    readonly security_recipients?: Record<string, boolean>;
  },
): Promise<SiteNotificationPreferencesResult> {
  try {
    const body = await http.request<SiteNotificationPreferences>({
      method: 'PATCH',
      path: `/v1/sites/${encodeURIComponent(site_id)}/notification-preferences`,
      body: patch as Record<string, unknown>,
    });
    if (body === undefined) return { kind: 'error', message: 'empty_response' };
    return { kind: 'ok', value: body };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') return { kind: 'bad_request', message: err.message };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'network_error',
    };
  }
}
